import { useState, useEffect, useRef } from 'react';
import {
  X,
  ShoppingCart,
  Package,
  Truck,
  AlertCircle,
  Plus,
  Trash2,
  Boxes,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { EmptyState, Skeleton } from '../../components/boothly/index.js';
import { Portal } from '../../components/boothly/portal.jsx';
import { useToast } from '../../components/boothly/toast-provider.jsx';
import { Z } from '../../lib/z-index.js';
import { fmtCurrency } from '../../lib/format.js';

// ── StockIndicator ────────────────────────────────────────────────────────────
// Nur für batch-getrackte Produkte/Batches. (Flat-Produkte erscheinen gar nicht.)
function StockIndicator({ stock }) {
  let status;
  if (stock > 10) status = 'ok';
  else if (stock > 3) status = 'low';
  else status = 'critical';

  const config = {
    ok:       { color: 'bg-ios-green',  label: 'OK' },
    low:      { color: 'bg-ios-orange', label: 'Niedrig' },
    critical: { color: 'bg-ios-red',    label: 'Kritisch' },
  };
  const { color, label } = config[status];

  return (
    <div className="flex items-center gap-2">
      {status === 'critical' && <AlertCircle className="h-4 w-4 text-ios-red" />}
      <div className={`h-2 w-2 rounded-full ${color}`} />
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

// ── StatusCard ────────────────────────────────────────────────────────────────
function StatusCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue:   'text-ios-blue   bg-ios-blue/20',
    purple: 'text-ios-purple bg-ios-purple/20',
    green:  'text-ios-green  bg-ios-green/20',
  };
  return (
    <div className="glass-card glass-shine rounded-xl p-3 text-center">
      <div className={`mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full ${colors[color]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

// Feld-Optik: klar gegen die Modal-Surface (dark #2C2C2E / light #FFFFFF) abgesetzt —
// --fill-secondary hellt im Dark auf / bleibt im Light dezent (per Mode korrekt) + Hairline.
// Wert-Text in --label-primary, Placeholder über muted-foreground (--label-secondary).
const inputCls =
  'w-full rounded-xl border border-[var(--border-default)] bg-[var(--fill-secondary)] px-4 py-3 text-sm text-[var(--label-primary)] outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ios-blue/50';

// Pflichtfeld-Marker (Akzentfarbe)
const Req = () => <span style={{ color: 'var(--accent-online)' }}>*</span>;

// ── LagerSheet ────────────────────────────────────────────────────────────────
// Zwei Modi: unscoped (aus Schnellzugriff) = Produktliste mit Bestand; scoped via
// store.lagerProductId (aus Produkt-Detail „Lager verwalten") = Produkt-Batch-Ansicht.
// Wareneingang läuft als Popup/Modal (Portal, nested-Z). Bestand kommt vom Server.
export function LagerSheet({ open, onOpenChange, store, currentRole, dark, loading }) {
  const toast = useToast();

  // Wareneingang (Sammel-Lieferung): Positionsliste + optionales Datum/Notiz.
  const emptyPosition = () => ({ productId: store.lagerProductId ?? '', quantity: '', unitCost: '' });
  const [wareneingangOpen, setWareneingangOpen] = useState(false);
  const [positions, setPositions] = useState([emptyPosition()]);
  const [receivedAt, setReceivedAt] = useState('');
  const [note, setNote] = useState('');
  const [savingWareneingang, setSavingWareneingang] = useState(false);
  const backdropRef = useRef(null);

  // Scroll-Lock dahinter (iOS-fest): body-overflow + non-passiver touchmove-Block am
  // Backdrop. Cleanup beim Schließen. Der Lager-Body geht zusätzlich auf overflow-hidden
  // (siehe Klassen unten); der Modal-Body erlaubt vertikales Scrollen via touch-action.
  useEffect(() => {
    if (!wareneingangOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const backdrop = backdropRef.current;
    const block = (e) => e.preventDefault();
    if (backdrop) backdrop.addEventListener('touchmove', block, { passive: false });
    return () => {
      document.body.style.overflow = prevOverflow;
      if (backdrop) backdrop.removeEventListener('touchmove', block);
    };
  }, [wareneingangOpen]);

  // Wareneingang (= Batch-Anlage) ist owner/admin (Server-Gate: finance).
  const canManage = ['owner', 'admin'].includes(currentRole);

  // Nur batch-getrackte, nicht-archivierte Produkte sind Lager-/Wareneingang-relevant.
  const batchProducts = (store.products || []).filter(p => p.costMode === 'batch' && !p.archivedAt);

  // Scope: aus dem Produkt-Sheet geöffnet (lagerProductId gesetzt).
  const scopedProduct = store.lagerProductId
    ? (store.products || []).find(p => p.id === store.lagerProductId) || null
    : null;

  // Batches des gescopten Produkts, sortiert nach created_at ASC = echte FIFO-
  // Verbrauchsreihenfolge (consumeBatchesFIFO sortiert created_at, NICHT received_at).
  const scopedBatches = scopedProduct
    ? (store.batches || [])
        .filter(b => b.productId === scopedProduct.id)
        .slice()
        .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
    : [];
  const scopedStock = scopedProduct ? store.getProductStock(scopedProduct.id) : 0;
  const scopedHasBatches = scopedBatches.length > 0;

  // Status counts (unscoped Übersicht)
  const offerCount     = store.counts?.Offen     ?? (store.orders ?? []).filter(o => o.status === 'Offen').length;
  const bereitCount    = store.counts?.Bereit    ?? 0;
  const versendetCount = store.counts?.Versendet ?? 0;

  // Sales by channel (unscoped, nur wenn Umsatz > 0)
  const channelTotals = (store.orders ?? [])
    .filter(o => o.status === 'Versendet' && o.preis)
    .reduce((acc, o) => {
      const ch = store.getChannelLabel?.(o.channelId) || o.kanal || 'Unbekannt';
      acc[ch] = (acc[ch] || 0) + (o.preis || 0);
      return acc;
    }, {});
  const totalSales = Object.values(channelTotals).reduce((a, b) => a + b, 0);

  // ── Wareneingang-Form ──────────────────────────────────────────────
  const openWareneingang = () => {
    setPositions([emptyPosition()]);
    setReceivedAt(new Date().toISOString().slice(0, 10)); // Default = heute
    setNote('');
    setWareneingangOpen(true);
  };
  const updatePosition = (idx, patch) =>
    setPositions(prev => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const addPosition = () => setPositions(prev => [...prev, { productId: '', quantity: '', unitCost: '' }]);
  const removePosition = (idx) => setPositions(prev => prev.filter((_, i) => i !== idx));

  const validPositions = positions.filter(p => p.productId && (parseInt(p.quantity, 10) || 0) > 0);
  const wareneingangTotal = positions.reduce((sum, p) => {
    const qty = parseInt(p.quantity, 10) || 0;
    const unit = parseFloat(p.unitCost) || 0;
    return sum + qty * unit;
  }, 0);
  const canSaveWareneingang = canManage && validPositions.length > 0 && !savingWareneingang;

  const handleSaveWareneingang = async () => {
    if (!canSaveWareneingang) return;
    const payloadPositions = validPositions.map(p => ({
      product_id: p.productId,
      quantity:   parseInt(p.quantity, 10),
      unit_cost:  parseFloat(p.unitCost) || 0,
    }));
    setSavingWareneingang(true);
    try {
      const payload = { positions: payloadPositions };
      // <input type="date"> liefert YYYY-MM-DD → ISO mit Offset für das Worker-Schema.
      if (receivedAt) payload.received_at = new Date(receivedAt).toISOString();
      if (note.trim()) payload.note = note.trim();
      const result = await store.apiDeliveryCreate(payload);
      if (result) {
        store.setBatches(prev => [...prev, ...result.batches]);
        const n = result.batches.length;
        toast.success(`Wareneingang gespeichert (${n} ${n === 1 ? 'Charge' : 'Chargen'})`);
        setWareneingangOpen(false);
      }
    } catch {
      toast.error('Wareneingang konnte nicht gespeichert werden');
    } finally {
      setSavingWareneingang(false);
    }
  };

  // Wareneingang-Button (in beiden Modi, nur owner/admin)
  const wareneingangButton = canManage ? (
    <button
      onClick={openWareneingang}
      data-testid="lager-wareneingang-btn"
      className="w-full flex items-center justify-center gap-2 rounded-full py-3.5 font-semibold text-white transition-transform active:scale-[0.98]"
      style={{ background: 'var(--accent-online)' }}
    >
      <Plus className="h-5 w-5" />
      Wareneingang
    </button>
  ) : null;

  // Produkt-Monogramm-Kachel
  const Monogram = ({ name }) => (
    <div
      className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-[15px] font-semibold"
      style={{ background: 'var(--tint-online)', color: 'var(--accent-online)' }}
    >
      {(name?.trim()?.[0] || '?').toUpperCase()}
    </div>
  );

  return (
    <>
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[20px]"
            style={{ animation: 'fadeIn 0.2s ease' }}
            onClick={() => onOpenChange(false)}
          />

          {/* Sheet */}
          <div
            data-testid="sheet-lager"
            className="fixed inset-x-0 bottom-0 z-50 flex max-h-[92vh] flex-col rounded-t-3xl"
            style={{
              paddingBottom: 'env(safe-area-inset-bottom)',
              animation: 'slideUpSheet 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
              background: dark ? '#2C2C2E' : '#FFFFFF',
            }}
          >
            <div className="flex justify-center pt-3 pb-2">
              <div className="bottom-sheet-handle" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-4">
              <div className="flex items-center gap-2 min-w-0">
                {scopedProduct && (
                  <button
                    onClick={() => store.setLagerProductId(null)}
                    className="ghost-icon-btn -ml-2"
                    aria-label="Zurück zur Lager-Übersicht"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                )}
                <div className="min-w-0">
                  <h2 className="text-xl font-semibold truncate">{scopedProduct ? scopedProduct.name : 'Lager'}</h2>
                  {scopedProduct && (
                    <p className="text-xs text-muted-foreground mt-0.5">Bestand {scopedStock} Stück</p>
                  )}
                </div>
              </div>
              <button onClick={() => onOpenChange(false)} className="ghost-icon-btn">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Scrollable body — während offenem Wareneingang gesperrt */}
            <div className={`flex-1 ${wareneingangOpen ? 'overflow-hidden' : 'overflow-y-auto'} px-5 pb-6 space-y-6`}>

              {scopedProduct ? (
                /* ── SCOPED: Produkt-Batch-Ansicht (FIFO nach created_at) ── */
                <>
                  {/* 0-Bestand-Banner ganz oben (nur bei stock === 0) */}
                  {scopedStock === 0 && (
                    <div
                      className="flex items-start gap-3 rounded-2xl p-4"
                      style={{
                        background: 'color-mix(in srgb, var(--accent-red) 12%, transparent)',
                        border: '1px solid color-mix(in srgb, var(--accent-red) 30%, transparent)',
                      }}
                    >
                      <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent-red)' }} />
                      <div className="min-w-0">
                        <p className="font-semibold" style={{ color: 'var(--accent-red)' }}>
                          {scopedHasBatches ? 'Kein Bestand – Nachkauf nötig' : 'Noch kein Bestand erfasst'}
                        </p>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {scopedHasBatches
                            ? `${scopedProduct.name} ist ausverkauft. Erfasse einen Wareneingang, um wieder Bestand anzulegen.`
                            : `${scopedProduct.name} hat noch keinen Bestand. Erfasse einen Wareneingang, um Bestand anzulegen.`}
                        </p>
                      </div>
                    </div>
                  )}

                  {wareneingangButton}

                  <div>
                    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Chargen (älteste zuerst)
                    </h3>
                    {loading ? (
                      <div className="space-y-3">
                        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}
                      </div>
                    ) : scopedBatches.length === 0 ? (
                      <EmptyState
                        icon={Boxes}
                        title="Noch keine Chargen"
                        description="Erfasse einen Wareneingang, um Bestand für dieses Produkt anzulegen."
                      />
                    ) : (
                      <div className="glass-card glass-shine rounded-2xl divide-y divide-border/50">
                        {scopedBatches.map((batch) => {
                          const stock = batch.remaining != null ? batch.remaining : (batch.stueckzahl || 0);
                          const date = batch.receivedAt
                            ? new Date(batch.receivedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })
                            : null;
                          return (
                            <div key={batch.id} data-testid={`lager-batch-${batch.id}`} className="flex items-center justify-between p-4">
                              <div className="min-w-0 flex-1">
                                <p className="font-medium">{stock} / {batch.stueckzahl} Stück</p>
                                <p className="text-sm text-muted-foreground">
                                  {fmtCurrency(batch.cogs || 0)} EK/Stück{date ? ` · ${date}` : ''}
                                </p>
                              </div>
                              <StockIndicator stock={stock} />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                /* ── UNSCOPED: Übersicht ── */
                <>
                  {/* Status-Übersicht (über dem Wareneingang-Button) */}
                  <div>
                    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Status-Übersicht
                    </h3>
                    <div className="grid grid-cols-3 gap-3">
                      <StatusCard icon={ShoppingCart} label="Offen"     value={offerCount}     color="blue"   />
                      <StatusCard icon={Package}      label="Bereit"    value={bereitCount}    color="purple" />
                      <StatusCard icon={Truck}        label="Versendet" value={versendetCount} color="green"  />
                    </div>
                  </div>

                  {wareneingangButton}

                  {/* Produktliste mit Bestand (nur batch-getrackte Produkte) */}
                  <div>
                    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Bestand pro Produkt
                    </h3>
                    {loading ? (
                      <div className="space-y-3">
                        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}
                      </div>
                    ) : batchProducts.length === 0 ? (
                      <EmptyState
                        icon={Boxes}
                        title="Keine batch-getrackten Produkte"
                        description="Stelle ein Produkt im Katalog auf den Kosten-Modus Aus Lager, dann erscheint sein Bestand hier."
                      />
                    ) : (
                      <div className="glass-card glass-shine rounded-2xl divide-y divide-border/50">
                        {batchProducts.map((p) => {
                          const stock = store.getProductStock(p.id);
                          return (
                            <button
                              key={p.id}
                              data-testid={`lager-product-${p.id}`}
                              onClick={() => store.setLagerProductId(p.id)}
                              className="w-full flex items-center gap-3 p-4 text-left active:opacity-90"
                            >
                              <Monogram name={p.name} />
                              <div className="min-w-0 flex-1">
                                <p className="font-medium truncate">{p.name}</p>
                                <p className="text-sm text-muted-foreground">{stock} auf Lager</p>
                              </div>
                              <StockIndicator stock={stock} />
                              <ChevronRight className="h-4 w-4 flex-shrink-0 text-[var(--label-tertiary)]" />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Verkäufe nach Kanal — nur wenn Umsatz > 0 */}
                  {totalSales > 0 && (
                    <div>
                      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Verkäufe nach Kanal
                      </h3>
                      <div className="glass-card glass-shine rounded-2xl p-4 space-y-4">
                        {Object.entries(channelTotals)
                          .sort((a, b) => b[1] - a[1])
                          .map(([channel, amount]) => {
                            const percent = totalSales > 0 ? Math.round((amount / totalSales) * 100) : 0;
                            return (
                              <div key={channel}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium">{channel}</span>
                                  <span className="text-sm text-muted-foreground">{fmtCurrency(amount)}</span>
                                </div>
                                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                                  <div className="h-full rounded-full bg-ios-blue" style={{ width: `${percent}%` }} />
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </>
              )}

            </div>
          </div>

          {/* ── Wareneingang-Popup (über dem Lager-Sheet, Portal + nested-Z) ── */}
          {wareneingangOpen && (
            <Portal>
              <div
                ref={backdropRef}
                onClick={() => setWareneingangOpen(false)}
                className="fixed inset-0 bg-black/40 backdrop-blur-[20px]"
                style={{ zIndex: Z.nestedBackdrop, animation: 'fadeIn 0.2s ease' }}
              />
              <div
                data-testid="sheet-wareneingang"
                className="fixed inset-x-0 bottom-0 flex max-h-[92vh] flex-col rounded-t-3xl"
                style={{
                  zIndex: Z.nestedSheet,
                  paddingBottom: 'env(safe-area-inset-bottom)',
                  animation: 'slideUpSheet 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
                  background: dark ? '#2C2C2E' : '#FFFFFF',
                }}
              >
                <div className="flex justify-center pt-3 pb-2">
                  <div className="bottom-sheet-handle" />
                </div>
                <div className="flex items-center justify-between px-5 pb-4">
                  <h2 className="text-xl font-semibold">Wareneingang</h2>
                  <button onClick={() => setWareneingangOpen(false)} className="ghost-icon-btn">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Modal-Body scrollt intern (touch-action: pan-y) */}
                <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-4" style={{ touchAction: 'pan-y' }}>
                  {batchProducts.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Keine batch-getrackten Produkte. Stelle ein Produkt im Katalog auf den
                      Kosten-Modus Aus Lager, um es hier zu beliefern.
                    </p>
                  ) : (
                    <>
                      {/* Positionen */}
                      <div className="space-y-3">
                        {positions.map((pos, idx) => (
                          <div key={idx} className="rounded-xl bg-secondary/40 p-3 space-y-2">
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-xs text-muted-foreground">Produkt <Req /></label>
                                {positions.length > 1 && (
                                  <button
                                    onClick={() => removePosition(idx)}
                                    className="ghost-icon-btn -mr-1 -my-1"
                                    aria-label="Position entfernen"
                                  >
                                    <Trash2 className="h-4 w-4 text-ios-red" />
                                  </button>
                                )}
                              </div>
                              <select
                                data-testid={`wareneingang-position-product-${idx}`}
                                value={pos.productId}
                                onChange={(e) => updatePosition(idx, { productId: e.target.value })}
                                className={inputCls}
                              >
                                <option value="">Produkt wählen…</option>
                                {batchProducts.map(p => (
                                  <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                              </select>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Stückzahl <Req /></label>
                                <input
                                  type="number"
                                  inputMode="numeric"
                                  placeholder="0"
                                  value={pos.quantity}
                                  onChange={(e) => updatePosition(idx, { quantity: e.target.value })}
                                  className={inputCls}
                                />
                              </div>
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">EK / Stück <Req /></label>
                                <div className="relative">
                                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">€</span>
                                  <input
                                    type="number"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    value={pos.unitCost}
                                    onChange={(e) => updatePosition(idx, { unitCost: e.target.value })}
                                    className={`${inputCls} pl-8`}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Produkt hinzufügen */}
                      <button
                        onClick={addPosition}
                        data-testid="wareneingang-add-position"
                        className="w-full flex items-center justify-center gap-2 rounded-xl bg-secondary/50 py-2.5 text-sm font-medium active:scale-[0.98] transition-transform"
                      >
                        <Plus className="h-4 w-4 text-ios-blue" />
                        Produkt hinzufügen
                      </button>

                      {/* Eingangsdatum (eigene Zeile, optional) */}
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Eingangsdatum</label>
                        <input
                          type="date"
                          value={receivedAt}
                          onChange={(e) => setReceivedAt(e.target.value)}
                          className={inputCls}
                        />
                      </div>

                      {/* Notiz (eigene Zeile, optional) */}
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Notiz</label>
                        <input
                          type="text"
                          placeholder="optional"
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                          className={inputCls}
                        />
                      </div>

                      <p className="text-xs text-muted-foreground"><Req /> Pflichtfeld</p>

                      {/* Summe */}
                      <div className="flex items-center justify-between rounded-xl bg-ios-blue/10 px-4 py-3">
                        <span className="text-sm font-medium">Summe Wareneinsatz</span>
                        <span className="text-lg font-bold text-ios-blue">{fmtCurrency(wareneingangTotal)}</span>
                      </div>

                      {/* Speichern */}
                      <button
                        onClick={handleSaveWareneingang}
                        disabled={!canSaveWareneingang}
                        data-testid="wareneingang-save"
                        className="w-full flex items-center justify-center gap-2 rounded-xl bg-ios-blue py-3 font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        Wareneingang speichern
                      </button>
                    </>
                  )}
                </div>
              </div>
            </Portal>
          )}
        </>
      )}
    </>
  );
}
